import { Boxes, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui';
import type { Emblem, TitleCard } from '../cosmetics';
import { EmblemGlyph } from './EmblemGlyph';

interface LockerSignpostProps {
  title: TitleCard;
  emblem: Emblem;
  onOpen: () => void;
}

/**
 * The Locker signpost: a snapshot of the equipped title + emblem and a button to
 * open the full Locker. The Locker itself is the single global cosmetics surface
 * (see `lockerStore`), so this panel only previews what's equipped.
 */
export function LockerSignpost({ title, emblem, onOpen }: LockerSignpostProps) {
  return (
    <section className="rounded-2xl border border-edge bg-white/[0.03] p-5">
      <div className="mb-4 flex items-center gap-2.5">
        <span className="grid h-9 w-9 flex-none place-items-center rounded-full bg-accent-soft text-accent">
          <Boxes size={18} strokeWidth={2} />
        </span>
        <div className="min-w-0">
          <div className="text-sm font-extrabold text-hi">Your Locker</div>
          <div className="text-[11px] text-mid">Themes, titles &amp; emblems</div>
        </div>
      </div>

      {/* Equipped title + emblem snapshot */}
      <div className="mb-4 space-y-2.5">
        <div className="relative h-20 overflow-hidden rounded-md border border-card-line">
          <div
            className="absolute inset-0 bg-cover bg-center"
            style={{ backgroundImage: `url('${title.bg}')` }}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
          <span className="absolute bottom-2 left-2.5 text-[11px] font-extrabold uppercase tracking-wide text-white drop-shadow">
            {title.name}
          </span>
        </div>
        <div className="flex items-center gap-2.5 rounded-md border border-edge bg-white/[0.02] px-3 py-2.5">
          <EmblemGlyph emblem={emblem} size={22} />
          <span className="text-xs font-semibold text-hi">{emblem.name}</span>
        </div>
      </div>

      <Button type="button" className="w-full" onClick={onOpen}>
        <Sparkles size={15} strokeWidth={2.2} />
        Open Locker
      </Button>
    </section>
  );
}
